"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  NumberResult,
  type ResultLine,
} from "@/components/tools/numbers-languages/number-result";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type HebrewNumeral,
  type HebrewOptions,
  type HebrewReadError,
  type HebrewReadNote,
  hebrewNumeral,
  MAX_HEBREW,
  readHebrewNumeral,
  stripHebrewMarks,
} from "@/lib/tool-kit/numbers-languages/hebrew";
import { readDecimalNumber } from "@/lib/tool-kit/numbers-languages/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const HEBREW_NUMERAL_CONVERTER_STRINGS = {
  directionLabel: "Convert",
  toHebrew: "Number → Hebrew letters",
  fromHebrew: "Hebrew letters → number",
  numberLabel: "Number",
  numberHint: "A whole number from 1 to {max}.",
  numberPlaceholder: "5786",
  hebrewLabel: "Hebrew letter numeral",
  hebrewHint:
    'With or without geresh and gershayim (ט״ו, טו, ט"ו); ה׳תשפ״ו reads the thousands too.',
  hebrewPlaceholder: "תשפ״ו",
  avoidDivine: "Write 15 and 16 as ט״ו and ט״ז (the standard)",
  ascii: "Use plain ' and \" instead of ׳ and ״",
  shortCount: "Leave out the thousands (short count, for years)",
  resultHeading: "In Hebrew letters",
  readHeading: "As a number",
  copy: "Copy",
  copied: "Copied!",
  breakdownLabel: "Letters and values",
  standardLabel: "Standard spelling",
  rulesHeading: "Why it is written this way",
  enterPrompt: "Type a number to see it in Hebrew letters.",
  enterHebrewPrompt: "Type a Hebrew letter numeral to read its value.",
  errorInvalid: "That is not a whole number. Use digits only.",
  errorRange:
    "Hebrew letter numerals cover whole numbers from 1 to {max}. There is no letter for zero.",
  errorReadInvalid: "These characters are not all Hebrew numeral letters.",
  errorReadOrder:
    "The letters must run from the largest value to the smallest, and only ת may repeat (up to twice).",
  errorReadTooBig: "A thousands letter can be only א to ט (1 to 9).",
  noteGeresh:
    "A single-letter numeral takes a geresh (׳) after the letter, so it is not mistaken for a word.",
  noteGershayim:
    "A numeral of two or more letters takes a gershayim (״) before its last letter.",
  noteDivineAvoided:
    "15 and 16 are written ט״ו (9+6) and ט״ז (9+7), because י״ה and י״ו spell forms of the divine name.",
  noteDivineLiteral:
    "Written as the plain sum (10+5, 10+6). The standard convention is ט״ו and ט״ז instead.",
  noteThousands:
    "Thousands come first as a single letter with a geresh, then the rest: ה׳ for 5000 and תשפ״ו for 786.",
  noteShortCount:
    "The thousands are left out, as Hebrew years are usually written (the short count).",
  noteRoundThousands:
    "Exact thousands are written with the word for thousand (אלף, אלפיים, ג׳ אלפים), because א׳ alone means 1.",
  noteReadThousands:
    "A letter followed by a geresh and more letters is read as the thousands.",
  noteReadFinal:
    "The final letters ך ם ן ף ץ are read as 500 to 900, the medieval variant. Most writers use תק to תתק instead.",
  noteReadDivine:
    "This is the literal sum for 15 or 16; the standard spelling avoids it (ט״ו, ט״ז).",
} as const;

type Strings = typeof HEBREW_NUMERAL_CONVERTER_STRINGS;
type Direction = "toHebrew" | "fromHebrew";

const READ_NOTE_KEYS: Record<HebrewReadNote, keyof Strings> = {
  thousands: "noteReadThousands",
  finalLetters: "noteReadFinal",
  divineName: "noteReadDivine",
};

const READ_ERROR_KEYS: Record<HebrewReadError, keyof Strings | null> = {
  empty: null,
  invalid: "errorReadInvalid",
  order: "errorReadOrder",
  tooBig: "errorReadTooBig",
};

interface View {
  words: string;
  lang: string;
  dir?: "rtl";
  lines: ResultLine[];
  notes: string[];
}

type Outcome = { view: View } | { error: string } | null;

function breakdown(numeral: HebrewNumeral): string {
  return numeral.parts.map((p) => `${p.value} ${p.letter}`).join(" + ");
}

function writeNotes(
  n: number,
  numeral: HebrewNumeral,
  options: HebrewOptions,
  strings: Strings
): string[] {
  const notes: string[] = [];
  notes.push(
    numeral.parts.length === 1 && n < 1000
      ? strings.noteGeresh
      : strings.noteGershayim
  );
  if ([15, 16].includes(n % 100) && n % 1000 !== 0) {
    notes.push(
      options.avoidDivineNames
        ? strings.noteDivineAvoided
        : strings.noteDivineLiteral
    );
  }
  if (n % 1000 === 0) {
    notes.push(strings.noteRoundThousands);
  } else if (n >= 1000) {
    notes.push(
      options.shortCount ? strings.noteShortCount : strings.noteThousands
    );
  }
  return notes;
}

function writeOutcome(
  raw: string,
  options: HebrewOptions,
  strings: Strings
): Outcome {
  const read = readDecimalNumber(raw);
  if (!read.ok) {
    return read.error === "empty" ? null : { error: strings.errorInvalid };
  }
  const { value } = read;
  const range = t(strings.errorRange, { max: String(MAX_HEBREW) });
  if (value.negative || value.fraction !== "" || value.integer.length > 5) {
    return { error: range };
  }
  const n = Number(value.integer);
  const numeral = hebrewNumeral(n, options);
  if (numeral === null) {
    return { error: range };
  }
  return {
    view: {
      words: numeral.text,
      lang: "he",
      dir: "rtl",
      lines: [
        { label: strings.breakdownLabel, text: breakdown(numeral), lang: "he" },
      ],
      notes: writeNotes(n, numeral, options, strings),
    },
  };
}

function readOutcome(raw: string, strings: Strings): Outcome {
  const result = readHebrewNumeral(raw);
  if (!result.ok) {
    const key = READ_ERROR_KEYS[result.error];
    return key === null ? null : { error: strings[key] };
  }
  const lines: ResultLine[] = [];
  const standard = hebrewNumeral(result.value, {
    avoidDivineNames: true,
    ascii: false,
    shortCount: false,
  });
  if (
    standard !== null &&
    stripHebrewMarks(standard.text) !== stripHebrewMarks(raw)
  ) {
    lines.push({
      label: strings.standardLabel,
      text: standard.text,
      lang: "he",
    });
  }
  return {
    view: {
      words: String(result.value),
      lang: "en",
      lines,
      notes: result.notes.map((note) => strings[READ_NOTE_KEYS[note]]),
    },
  };
}

export default function HebrewNumeralConverterWidget() {
  const strings = useToolUi(HEBREW_NUMERAL_CONVERTER_STRINGS);
  const [direction, setDirection] = useState<Direction>("toHebrew");
  const [raw, setRaw] = useState("5786");
  const [hebrew, setHebrew] = useState("");
  const [options, setOptions] = useState<HebrewOptions>({
    avoidDivineNames: true,
    ascii: false,
    shortCount: false,
  });
  const set = <K extends keyof HebrewOptions>(
    key: K,
    value: HebrewOptions[K]
  ) => setOptions((current) => ({ ...current, [key]: value }));
  const forward = direction === "toHebrew";
  const outcome = forward
    ? writeOutcome(raw, options, strings)
    : readOutcome(hebrew, strings);

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.directionLabel}
        name="he-direction"
        onChange={setDirection}
        options={[
          { value: "toHebrew", label: strings.toHebrew },
          { value: "fromHebrew", label: strings.fromHebrew },
        ]}
        value={direction}
      />
      {forward ? (
        <TextField
          hint={t(strings.numberHint, { max: String(MAX_HEBREW) })}
          id="he-number"
          label={strings.numberLabel}
          numeric
          onChange={setRaw}
          placeholder={strings.numberPlaceholder}
          value={raw}
        />
      ) : (
        <TextField
          hint={strings.hebrewHint}
          id="he-hebrew"
          label={strings.hebrewLabel}
          onChange={setHebrew}
          placeholder={strings.hebrewPlaceholder}
          value={hebrew}
        />
      )}
      {forward ? (
        <div className="space-y-2">
          <Checkbox
            checked={options.avoidDivineNames}
            label={strings.avoidDivine}
            onChange={(value) => set("avoidDivineNames", value)}
          />
          <Checkbox
            checked={options.shortCount}
            label={strings.shortCount}
            onChange={(value) => set("shortCount", value)}
          />
          <Checkbox
            checked={options.ascii}
            label={strings.ascii}
            onChange={(value) => set("ascii", value)}
          />
        </div>
      ) : null}
      {outcome === null ? (
        <Notice>
          {forward ? strings.enterPrompt : strings.enterHebrewPrompt}
        </Notice>
      ) : null}
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "view" in outcome ? (
        <NumberResult
          dir={outcome.view.dir}
          lang={outcome.view.lang}
          lines={outcome.view.lines}
          notes={outcome.view.notes}
          strings={{
            heading: forward ? strings.resultHeading : strings.readHeading,
            copy: strings.copy,
            copied: strings.copied,
            rulesHeading: strings.rulesHeading,
          }}
          words={outcome.view.words}
        />
      ) : null}
    </div>
  );
}
